import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { size, isString } from 'lodash';
import { IconButton } from '@mui/material';

import { datePickerFormat } from '../../services/ValidationRules';
import TextField from '../TextField/TextField';
import { DatePicker } from '../DateTimePickers/DateTimePickers';

const formatForApi = 'MM/DD/YYYY'; // TODO: need to remove after migrate api to timestamp usage

export function CalendarIcon({ width = 16, height = 16 }) {
  return (
    <svg width={width} height={height} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5.3335 1.33337V3.33337"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.6665 1.33337V3.33337"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.3335 6.06006H13.6668"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 5.66671V11.3334C14 13.3334 13 14.6667 10.6667 14.6667H5.33333C3 14.6667 2 13.3334 2 11.3334V5.66671C2 3.66671 3 2.33337 5.33333 2.33337H10.6667C13 2.33337 14 3.66671 14 5.66671Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// TODO: need to refactor -> UTC mode as option, now it's used for multiFwUpdates, only for timestampOutput mode
function RenderDatepickerFilter(props) {
  const { onChange, timestampOutput, value } = props;

  const [opened, setOpened] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef(null);

  const forwardChange = useCallback(onChange, [onChange]);

  const processDateStringFromApiToLocalFormat = (newValue) => {
    const dateStringInMoment = moment.utc(newValue, formatForApi, true);

    if (newValue && dateStringInMoment.isValid()) {
      return dateStringInMoment.format(datePickerFormat);
    }
  };
  const processTimestampOutputValue = (newValue) => {
    if (!newValue) return '';
    const timestampInMoment = moment.unix(newValue / 1000);

    return timestampInMoment.isValid() ? timestampInMoment.utc().format(datePickerFormat) : newValue;
  };
  const handleChangeDatePicker = (date) => {
    const momentDate = moment(date);
    const formattedLocalDate = momentDate.format(datePickerFormat);
    const formattedApiDate = momentDate.format(formatForApi);

    setInputValue(formattedLocalDate);
    if (timestampOutput) {
      onChange(momentDate.valueOf().toString());
    } else {
      onChange(formattedApiDate);
    }
  };

  const handleDateInputBlur = () => {
    const momentDate = moment.utc(inputValue, datePickerFormat, true);

    if (momentDate.isValid()) {
      const formattedValue = timestampOutput ? momentDate.valueOf().toString() : momentDate.format(formatForApi);

      forwardChange(formattedValue);

      return;
    }

    if (inputValue === '') forwardChange(inputValue);
  };

  const openDatePicker = () => setOpened(true);
  const closeDatePicker = () => setOpened(false);
  const convertInputToDate = (newLocalValue) => {
    const momentDate = moment.utc(isString(newLocalValue) ? newLocalValue : '', datePickerFormat, true);

    return momentDate.isValid() ? momentDate : moment.utc();
  };

  const handleDateInputChange = (event) => {
    const text = event.target.value;
    const momentDate = moment.utc(text, datePickerFormat, true);

    setInputValue(text);
    if (momentDate.isValid()) {
      if (timestampOutput) {
        forwardChange(momentDate.valueOf().toString());
      } else {
        forwardChange(momentDate.format(formatForApi));
      }
    } else if (text === '') {
      forwardChange('');
    }
  };

  const checkOnError = (newLocalValue) => {
    let errorFormatText = '';

    if (size(newLocalValue)) {
      if (timestampOutput) {
        errorFormatText = '';
      } else {
        errorFormatText = moment(newLocalValue, datePickerFormat, true).isValid() ? null : 'Incorrect Format';
      }
    }

    return errorFormatText;
  };

  const wrapperClass = 'date-picker-filter-container';
  const datePickerDate = convertInputToDate(inputValue);
  const errorFormatText = checkOnError(inputValue);
  const hintText = !inputValue ? datePickerFormat : '';
  const iconButton = (
    <IconButton size="large" className="calendarIcon" onClick={openDatePicker}>
      <CalendarIcon />
    </IconButton>
  );

  useEffect(() => {
    if (timestampOutput) {
      setInputValue(processTimestampOutputValue(value) || '');

      return;
    }
    setInputValue(processDateStringFromApiToLocalFormat(value) || '');
  }, [value]);

  return (
    <div className={wrapperClass}>
      <TextField
        name="render_datePicker_filter_text"
        errorText={errorFormatText}
        placeholder={hintText}
        value={inputValue}
        fullWidth
        inputRef={inputRef}
        onChange={handleDateInputChange}
        InputProps={{
          onBlur: handleDateInputBlur,
        }}
      />

      {iconButton}

      <div className="hidden-datePicker">
        <DatePicker
          value={datePickerDate}
          open={opened}
          onAccept={handleChangeDatePicker}
          onClose={closeDatePicker}
          format={datePickerFormat}
          anchorEl={inputRef.current}
          isMobile
          hideClearBtn
        />
      </div>
    </div>
  );
}

RenderDatepickerFilter.propTypes = {
  opts: PropTypes.any,
  value: PropTypes.any,
  timestampOutput: PropTypes.bool,
  onChange: PropTypes.func,
};

export default RenderDatepickerFilter;
